import React from 'react';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useGamificationContext } from '../../context/GamificationContext';
import PremiumLock from '../../components/Common/PremiumLock';
import { PortfolioPerformanceItem, usePortfolioAnalysis } from './usePortfolioAnalysis';
import AllocationChart from './components/AllocationChart';
import Disclaimer from '../../components/Common/Disclaimer';
import useScreenType from '../../hooks/useScreenType';
import CompanyLogo from '../../components/Common/CompanyLogo';
import MobilePortfolioAnalysis from './MobilePortfolioAnalysis';
import {
  AlertesComportementales,
  EvolutionCard,
  KpiCard,
  PeriodesCard,
  note,
  titreSection,
} from './components/PortfolioWidgets';
import {
  cours,
  couleur,
  dateLongue,
  entier,
  fcfa,
  fcfaSigne,
  pct,
  pctSigne,
  ratio,
} from './portfolioFormat';

type Periode = 'jour' | 'semaine' | 'mois' | 'annee';

const PERIODES: { cle: Periode; libelle: string }[] = [
  { cle: 'jour', libelle: 'Jour' },
  { cle: 'semaine', libelle: 'Semaine' },
  { cle: 'mois', libelle: 'Mois' },
  { cle: 'annee', libelle: 'Depuis le 1er janv.' },
];

/** Préférence d'affichage propre au navigateur : son absence ramène simplement au jour. */
const CLE_PERIODE = 'kanea.analysePortefeuille.periode';

const lirePeriode = (): Periode => {
  try {
    const valeur = window.localStorage.getItem(CLE_PERIODE);
    return PERIODES.some((p) => p.cle === valeur) ? (valeur as Periode) : 'jour';
  } catch {
    return 'jour';
  }
};

const ecrirePeriode = (periode: Periode) => {
  try {
    window.localStorage.setItem(CLE_PERIODE, periode);
  } catch {
    // stockage indisponible (navigation privée) : le choix vaut pour la session en cours
  }
};

/** Variation de la ligne sur la période choisie. Le montant depuis le 1er janvier n'est pas servi par ligne. */
const variationPeriode = (
  item: PortfolioPerformanceItem,
  periode: Periode
): { pct: number | null; montant?: number } => {
  switch (periode) {
    case 'jour':
      return { pct: item.pl_day_pct, montant: item.pl_day_value };
    case 'semaine':
      return { pct: item.pl_week_pct, montant: item.pl_week_value };
    case 'mois':
      return { pct: item.pl_month_pct, montant: item.pl_month_value };
    default:
      return { pct: item.perf_ytd_pct };
  }
};

type CleTri =
  | 'symbol'
  | 'current_value'
  | 'perf_absolute'
  | 'periode'
  | 'rendement_brut_pct'
  | 'volatility_pct';

const COLONNES: { cle: CleTri | null; libelle: string; aide?: string }[] = [
  { cle: 'symbol', libelle: 'Titre' },
  { cle: null, libelle: 'Qté' },
  { cle: null, libelle: 'PRU', aide: 'Prix de revient unitaire moyen, hors frais de courtage' },
  { cle: null, libelle: 'Cours' },
  {
    cle: 'current_value',
    libelle: 'Valorisation',
    aide: 'Valeur de la ligne au dernier cours (F CFA) et poids dans le portefeuille',
  },
  {
    cle: 'perf_absolute',
    libelle: 'Plus-value',
    aide: "Plus-value latente avant frais (F CFA) et variation depuis l'achat",
  },
  { cle: 'periode', libelle: 'Performance', aide: 'Sur la période choisie au-dessus du tableau' },
  {
    cle: 'rendement_brut_pct',
    libelle: 'Rendement',
    aide: "Dividende annuel brut / valeur de la ligne. Survolez une valeur pour le net de l'IRVM.",
  },
  {
    cle: 'volatility_pct',
    libelle: 'Volatilité',
    aide: 'Volatilité annualisée sur les 30 dernières séances',
  },
];

const valeurTri = (
  item: PortfolioPerformanceItem,
  cle: CleTri,
  periode: Periode
): number | string => {
  if (cle === 'symbol') return item.symbol;
  const valeur = cle === 'periode' ? variationPeriode(item, periode).pct : item[cle];
  return valeur ?? Number.NEGATIVE_INFINITY;
};

const celluleNombre: React.CSSProperties = {
  padding: '10px 8px',
  textAlign: 'right',
  color: 'var(--text-main)',
  whiteSpace: 'nowrap',
};

const sousTexte: React.CSSProperties = {
  fontSize: '11px',
  color: 'var(--text-muted)',
  fontWeight: 400,
};

/** Cellule à deux étages : la valeur principale, et en dessous son complément plus discret. */
const CelluleDouble: React.FC<{
  haut: string;
  bas?: string;
  couleurHaut?: string;
  gras?: boolean;
  title?: string;
}> = ({ haut, bas, couleurHaut, gras, title }) => (
  <td style={celluleNombre} title={title}>
    <div style={{ fontWeight: gras ? 600 : 400, color: couleurHaut ?? 'var(--text-main)' }}>
      {haut}
    </div>
    {bas !== undefined && <div style={sousTexte}>{bas}</div>}
  </td>
);

const montantSigne = (montant: number) => `${Math.round(montant) > 0 ? '+' : ''}${entier(montant)}`;

const aideRendement = (item: PortfolioPerformanceItem): string | undefined => {
  if (item.rendement_brut_pct === null || item.rendement_brut_pct === undefined) return undefined;
  const lignes = [
    `Brut : ${pct(item.rendement_brut_pct, 2)} · dividende annuel ${fcfa(item.expected_annual_dividend)}`,
    item.rendement_net_pp_pct !== null && item.rendement_net_pp_pct !== undefined
      ? `Net de l'IRVM, personnes physiques (${item.country}) : ${pct(item.rendement_net_pp_pct, 2)}`
      : "Net de l'IRVM indisponible",
  ];
  if (item.dividend_was_normalized)
    lignes.push('Dividende exceptionnel écarté (médiane sur 5 ans)');
  return lignes.join('\n');
};

const PortfolioAnalysisScreen: React.FC = () => {
  const { hasPermission } = useAuth();
  const { trackAction } = useGamificationContext();
  const { data, loading, error } = usePortfolioAnalysis();
  const navigate = useNavigate();
  const { isMobile } = useScreenType();
  const [tri, setTri] = React.useState<{ cle: CleTri; desc: boolean }>({
    cle: 'current_value',
    desc: true,
  });
  const [periode, setPeriode] = React.useState<Periode>(lirePeriode);

  const choisirPeriode = (p: Periode) => {
    setPeriode(p);
    ecrirePeriode(p);
  };

  React.useEffect(() => {
    if (hasPermission('view_advanced_data')) {
      trackAction('VIEW_PORTFOLIO_ANALYSIS');
    }
  }, [hasPermission, trackAction]);

  const lignes = React.useMemo(() => {
    const items = [...(data?.performance_by_asset ?? [])];
    items.sort((a, b) => {
      const va = valeurTri(a, tri.cle, periode);
      const vb = valeurTri(b, tri.cle, periode);
      const ordre = typeof va === 'string' ? va.localeCompare(String(vb)) : va - (vb as number);
      return tri.desc ? -ordre : ordre;
    });
    return items;
  }, [data, tri, periode]);

  if (!hasPermission('view_advanced_data')) {
    return (
      <SiteLayout>
        <Header title='Analyse du Portefeuille' />
        <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
          <PremiumLock
            title='Analyse Avancée du Portefeuille'
            description='Visualisez la performance réelle de vos actifs, votre allocation sectorielle et géographique, etc. Disponible avec le plan Kanea Starter ou supérieur. 💼'
          />
        </div>
      </SiteLayout>
    );
  }

  if (loading) {
    return (
      <SiteLayout>
        <Header title='Analyse du Portefeuille' />
        <div style={{ padding: '40px 20px', textAlign: 'center', ...note }}>
          Chargement de votre analyse…
        </div>
      </SiteLayout>
    );
  }

  if (error) {
    return (
      <SiteLayout>
        <Header title='Analyse du Portefeuille' />
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
          <Box>
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-main)' }}>
              {error}
            </div>
          </Box>
        </div>
      </SiteLayout>
    );
  }

  if (!data || data.total_value === 0) {
    return (
      <SiteLayout>
        <Header title='Analyse du Portefeuille' />
        <Box>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <h3 style={{ color: 'var(--text-heading)' }}>Votre portefeuille est vide</h3>
            <p style={{ color: 'var(--text-main)' }}>Ajoutez des actifs pour voir l'analyse.</p>
            <button
              onClick={() => navigate('/transactions')}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                marginTop: '10px',
              }}
            >
              Ajouter une transaction
            </button>
          </div>
        </Box>
      </SiteLayout>
    );
  }

  const changerTri = (cle: CleTri) =>
    setTri((t) => (t.cle === cle ? { cle, desc: !t.desc } : { cle, desc: cle !== 'symbol' }));

  const sharpeDetail =
    data.sharpe_like_ratio !== null
      ? `Sur ${data.history_days_available} séances, hors apports, taux sans risque ${pct(data.risk_free_rate_pct, 1)}`
      : "Disponible après 20 séances d'historique";

  return (
    <SiteLayout>
      <Header title='Analyse du Portefeuille' />
      {isMobile ? (
        <MobilePortfolioAnalysis data={data} />
      ) : (
        <div
          style={{
            padding: '20px',
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}
        >
          <div style={{ ...note, textAlign: 'right' }}>
            Cours de clôture du{' '}
            <strong style={{ color: 'var(--text-main)' }}>{dateLongue(data.cours_au)}</strong>
          </div>

          {/* 1. L'essentiel */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '20px',
            }}
          >
            <KpiCard
              libelle='Valeur des titres'
              valeur={fcfa(data.total_value)}
              detail='Liquidités non comprises'
            />
            <KpiCard
              libelle='Plus-value latente'
              valeur={fcfaSigne(data.total_profit_loss)}
              couleurValeur={couleur(data.total_profit_loss)}
              detail={
                <span style={{ color: couleur(data.total_profit_loss_pct) }}>
                  {pctSigne(data.total_profit_loss_pct)} sur le prix de revient
                </span>
              }
              aide='Valeur actuelle − prix de revient des titres détenus. Avant frais de courtage ; hors plus-values réalisées et dividendes reçus.'
            />
            <KpiCard
              libelle='Dividendes annuels estimés'
              valeur={fcfa(data.expected_annual_dividends)}
              detail={`Bruts, avant retenue IRVM · rendement brut ${pct(data.portfolio_yield_pct, 2)}`}
              aide="Dernier dividende par action connu × titres détenus, corrigé des dividendes exceptionnels. Montant brut : la retenue IRVM dépend du pays de l'émetteur."
            />
            <KpiCard
              libelle='Frais de courtage cumulés'
              valeur={fcfa(data.total_fees)}
              couleurValeur='#ef4444'
              detail='Non déduits de la plus-value'
            />
          </div>

          {/* 2. Performances par période */}
          <PeriodesCard data={data} />

          {/* 3. Évolution */}
          <EvolutionCard data={data} hauteur={300} />

          {/* 4. Allocation */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '20px',
            }}
          >
            <AllocationChart title='Allocation sectorielle' data={data.sector_allocation} />
            <AllocationChart title='Allocation géographique' data={data.country_allocation} />
          </div>

          {/* 5. Risque et valorisation */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '20px',
            }}
          >
            <KpiCard
              libelle='Volatilité du portefeuille'
              valeur={pct(data.portfolio_volatility_pct, 1)}
              detail={
                data.portfolio_volatility_pct !== null
                  ? 'Annualisée, hors apports'
                  : "Disponible après 20 séances d'historique"
              }
              aide='Écart-type annualisé des rendements journaliers du portefeuille, achats et ventes neutralisés.'
            />
            <KpiCard
              libelle='Ratio de Sharpe'
              valeur={ratio(data.sharpe_like_ratio)}
              detail={sharpeDetail}
              aide="(Rendement annualisé − taux sans risque) / volatilité annualisée. Sur une fenêtre courte, il varie fortement d'un mois à l'autre."
            />
            <KpiCard
              libelle='PER du portefeuille'
              valeur={ratio(data.weighted_per)}
              detail='Valeur des titres / bénéfices au prorata'
              aide='Moyenne harmonique pondérée des PER Kanea. Les titres sans PER positif sont exclus.'
            />
          </div>

          {/* 6. Détail par titre */}
          <Box>
            <div style={{ padding: '20px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px',
                  marginBottom: '16px',
                }}
              >
                <h3 style={{ ...titreSection, margin: 0 }}>Performance par titre</h3>
                <div
                  role='group'
                  aria-label='Période de la colonne Performance'
                  style={{
                    display: 'flex',
                    background: 'var(--bg-input)',
                    borderRadius: '8px',
                    padding: '3px',
                  }}
                >
                  {PERIODES.map((p) => (
                    <button
                      key={p.cle}
                      type='button'
                      aria-pressed={periode === p.cle}
                      onClick={() => choisirPeriode(p.cle)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: 'none',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: periode === p.cle ? '#2563eb' : 'transparent',
                        color: periode === p.cle ? '#ffffff' : 'var(--text-muted)',
                      }}
                    >
                      {p.libelle}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                      {COLONNES.map((c, i) => (
                        <th
                          key={c.libelle}
                          title={c.aide}
                          onClick={c.cle ? () => changerTri(c.cle as CleTri) : undefined}
                          style={{
                            padding: '8px',
                            textAlign: i === 0 ? 'left' : 'right',
                            color: 'var(--text-muted)',
                            fontWeight: 600,
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            cursor: c.cle ? 'pointer' : 'default',
                            userSelect: 'none',
                          }}
                        >
                          {c.libelle}
                          {c.cle && tri.cle === c.cle && (tri.desc ? ' ▼' : ' ▲')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lignes.map((item) => {
                      const variation = variationPeriode(item, periode);
                      const depuisAchat = periode === 'annee' && item.perf_ytd_base === 'ACHAT';
                      return (
                        <tr
                          key={item.symbol}
                          style={{ borderBottom: '1px solid var(--border-color)' }}
                        >
                          <td style={{ padding: '10px 8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <CompanyLogo symbol={item.symbol} size={28} />
                              <div style={{ minWidth: 0 }}>
                                <button
                                  type='button'
                                  style={{
                                    font: 'inherit', // avant fontWeight : le raccourci `font` le réinitialise
                                    fontWeight: 700,
                                    color: '#2563eb',
                                    cursor: 'pointer',
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                  }}
                                  onClick={() => navigate(`/data?symbol=${item.symbol}`)}
                                >
                                  {item.symbol}
                                </button>
                                {item.data_unavailable && (
                                  <span
                                    title={item.data_unavailable_reason ?? 'Donnée indisponible'}
                                    style={{
                                      marginLeft: '6px',
                                      fontSize: '10px',
                                      fontWeight: 700,
                                      color: '#f59e0b',
                                      border: '1px solid #f59e0b',
                                      borderRadius: '4px',
                                      padding: '1px 4px',
                                      cursor: 'help',
                                    }}
                                  >
                                    ⚠ Cours indisponible
                                  </span>
                                )}
                                <div
                                  style={{
                                    ...sousTexte,
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {item.stock_name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td style={celluleNombre}>{entier(item.quantity)}</td>
                          <td style={celluleNombre}>{cours(item.pru)}</td>
                          <td style={celluleNombre}>{cours(item.current_price)}</td>
                          <CelluleDouble
                            haut={entier(item.current_value)}
                            bas={pct(item.weight_pct)}
                            gras
                          />
                          <CelluleDouble
                            haut={montantSigne(item.perf_absolute)}
                            bas={pctSigne(item.perf_pct)}
                            couleurHaut={couleur(item.perf_absolute)}
                          />
                          <CelluleDouble
                            haut={`${pctSigne(variation.pct)}${depuisAchat ? '*' : ''}`}
                            bas={
                              variation.montant !== undefined
                                ? montantSigne(variation.montant)
                                : undefined
                            }
                            couleurHaut={couleur(variation.pct)}
                            gras
                            title={
                              depuisAchat
                                ? "Acheté en cours d'année : performance depuis l'achat"
                                : undefined
                            }
                          />
                          <td
                            style={{ ...celluleNombre, cursor: 'help' }}
                            title={aideRendement(item)}
                          >
                            {pct(item.rendement_brut_pct, 2)}
                          </td>
                          <td style={celluleNombre}>{pct(item.volatility_pct)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p style={{ ...note, margin: '12px 0 0 0' }}>
                Valorisation et plus-values en F CFA, au dernier cours, avant frais de courtage.
                Rendement : dividende annuel brut, avant IRVM, rapporté à la valeur de la ligne.
                {periode === 'annee' &&
                  " * Titre acheté en cours d'année : performance depuis l'achat."}{' '}
                Volatilité annualisée sur 30 séances.
              </p>
            </div>
          </Box>

          {/* 7. Alertes comportementales (Pro) */}
          <AlertesComportementales />

          <Disclaimer type='investment' compact />
        </div>
      )}
    </SiteLayout>
  );
};

export default PortfolioAnalysisScreen;
